import { Typography } from '@mui/material';
import { styled } from '@mui/system';
import { history } from 'app';
import { Location } from 'history';
import Button from 'new/components/Button/Button';
import { useEffect } from 'react';
import { WithRouterProps, withRouter } from 'react-router';
import create, { SetState } from 'zustand';
import IconFontAwesome from './IconFontAwesome';
import LinkButton from './LinkButton';
import Modal from './Modal';
import { ModalContent } from './ModalContent/styles';

type Props<NavigateProps> = {
  when?: boolean;
  navigate?: (path: string, navigateProps?: NavigateProps) => void;
  shouldBlockNavigation?: (location: Location) => boolean;
};

const RouteLeavingModalContent = styled(ModalContent)(() => ({
  width: 400,
  textAlign: 'center',
}));

type RouteLeavingGuardStore = {
  loaded: boolean;
  modalVisible: boolean;
  lastLocation: Location | null;
  confirmedNavigation: boolean;
  manualCheck: boolean;
  setLoaded: (loaded: boolean) => void;
  setModalVisible: (show: boolean) => void;
  setLastLocation: (location: Location | null) => void;
  setConfirmedNavigation: (confirmed: boolean) => void;
  setManualCheck: (check: boolean) => void;
  changeRoute: (
    route: Location,
    navigateProps?: Record<string, string | boolean | number>,
  ) => void;
  navigateProps?: Record<string, string | boolean | number>;
};

export const useRouteLeavingGuard = create<RouteLeavingGuardStore>(
  (set: SetState<RouteLeavingGuardStore>) => ({
    loaded: false,
    modalVisible: false,
    lastLocation: null,
    confirmedNavigation: false,
    manualCheck: false,
    navigateProps: undefined,
    setLoaded: (loaded: boolean) =>
      set((state) => {
        return { ...state, loaded: loaded };
      }),
    setModalVisible: (show: boolean) =>
      set((state) => {
        return { ...state, modalVisible: show };
      }),
    setLastLocation: (location: Location | null) =>
      set((state) => {
        return { ...state, lastLocation: location };
      }),
    setConfirmedNavigation: (confirmed: boolean) =>
      set((state) => {
        return { ...state, confirmedNavigation: confirmed };
      }),
    setManualCheck: (check: boolean) =>
      set((state) => {
        return { ...state, manualCheck: check };
      }),
    changeRoute: (
      location: Location,
      navigateProps?: Record<string, string | boolean | number>,
    ) =>
      set((state) => {
        if (state.loaded) {
          return {
            ...state,
            lastLocation: location,
            manualCheck: true,
            navigateProps,
          };
        }
        history.push(location.pathname + location.search);
        return state;
      }),
  }),
);

const RouteLeavingGuard = <NavigateProps,>({
  router: { setRouteLeaveHook, goForward },
  routes,
  when,
  navigate = (path: string) => history.push(path),
  shouldBlockNavigation = (location: Location) => {
    const currentLocation = history.getCurrentLocation();
    return (
      !!when &&
      location.pathname + location.search !=
        currentLocation.pathname + currentLocation.search
    );
  },
}: Props<NavigateProps> & WithRouterProps) => {
  const {
    modalVisible,
    setModalVisible,
    lastLocation,
    setLastLocation,
    confirmedNavigation,
    setConfirmedNavigation,
    manualCheck,
    setManualCheck,
    setLoaded,
    navigateProps,
  } = useRouteLeavingGuard();

  useEffect(() => {
    setLoaded(true);
    return () => setLoaded(false);
  }, []);

  useEffect(() => {
    if (when) {
      enablePrompt();
    } else {
      disablePrompt();
    }
    return disablePrompt();
  }, [when]);

  const closeModal = () => {
    setModalVisible(false);
    setManualCheck(false);
  };

  const enablePrompt = () => {
    setRouteLeaveHook(routes[1], handleBlockedNavigation);
  };

  const disablePrompt = () => setRouteLeaveHook(routes[1], goForward);

  const handleBlockedNavigation = (nextLocation?: Location): boolean => {
    if (
      !confirmedNavigation &&
      nextLocation &&
      shouldBlockNavigation(nextLocation)
    ) {
      setModalVisible(true);
      setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  const handleConfirmNavigationClick = () => {
    disablePrompt();
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Navigate to the previous blocked location with your navigate function
      navigate(
        lastLocation.pathname + lastLocation.search,
        navigateProps as NavigateProps,
      ); //TODO: better type
      setConfirmedNavigation(false);
      setLastLocation(null);
    }
  }, [confirmedNavigation, lastLocation]);

  useEffect(() => {
    if (manualCheck && lastLocation) {
      if (!confirmedNavigation && when && shouldBlockNavigation(lastLocation)) {
        setModalVisible(true);
      } else {
        navigate(
          lastLocation.pathname + lastLocation.search,
          navigateProps as NavigateProps,
        ); //TODO: better type
        setManualCheck(false);
      }
    } else {
      setManualCheck(false);
    }
  }, [manualCheck, lastLocation, when]);

  return (
    <>
      <Modal open={modalVisible} onClose={closeModal} title="ARE YOU SURE?">
        <RouteLeavingModalContent mt={2}>
          <IconFontAwesome
            name="faTriangleExclamationReg"
            color="warning"
            size="4x"
          />
          <Typography fontSize="0.875rem">
            You have <strong>unsaved changes</strong> on this page.
          </Typography>
          <Typography fontSize="0.875rem">
            If you proceed, you will lose your unsaved changes.
          </Typography>
          <Button onClick={handleConfirmNavigationClick}>
            YES, CANCEL CHANGES AND NAVIGATE
          </Button>
          <LinkButton onClick={closeModal}>
            <Typography fontStyle="italic">
              Never mind. Just close this window.
            </Typography>
          </LinkButton>
        </RouteLeavingModalContent>
      </Modal>
    </>
  );
};

export function getRouteLeavingGuard<NavigateProps>() {
  return withRouter((props: Props<NavigateProps> & WithRouterProps) => (
    <RouteLeavingGuard<NavigateProps> {...props} />
  ));
}

export default withRouter(RouteLeavingGuard);
